import { useState } from 'react';
import { FLOX_REGISTRATION_URL } from '../../constants';

export function PromptButton({
  text,
  icon,
  handleClick,
  promptMessage,
  fullWidth = false,
  primary = false,
}: {
  text: string;
  icon: string;
  handleClick: (message: string) => void;
  promptMessage?: string;
  fullWidth?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type='button'
      className={`flex items-center px-4 py-2 border-none rounded-lg shadow-sm focus:outline-none
        ${fullWidth ? 'w-full justify-center' : ''}
        ${
          primary
            ? 'bg-primary text-white hover:bg-blue-700'
            : 'bg-white text-black hover:bg-gray-100'
        }
        whitespace-normal`}
      onClick={() => handleClick(promptMessage || text)}
    >
      <img src={icon} alt='' className='w-5 h-5 mr-2 flex-shrink-0' />
      {text}
    </button>
  );
}

export function AIInput() {
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
  };

  const handleSendClick = (message: string) => {
    const sidebarElement = document.querySelector('[data-sidebar]');
    if (sidebarElement) {
      sidebarElement.setAttribute('data-open', 'true'); // Open the sidebar
      sidebarElement.setAttribute('data-message', message); // Send a message
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!inputValue) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendClick(inputValue);
    }
  };

  return (
    <div className='flex flex-col space-y-8 mt-1'>
      <div className='relative w-full max-w-[500px]'>
        <textarea
          id='aiInput'
          placeholder='Search for transport, warehouse space, and logistics services.'
          className='w-full px-4 py-4 pb-2 pr-12 border-none rounded-lg shadow-sm focus:outline-none focus:ring-0 focus:border-transparent resize-none placeholder:text-gray-400 placeholder:text-sm'
          rows={3}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
        <div className='absolute -top-2 bottom-0 right-0 flex items-center pr-2'>
          <button
            type='button'
            className={`flex items-center justify-center w-10 h-10 text-white border-none rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
              inputValue
                ? 'bg-blue-500 hover:bg-blue-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
            disabled={!inputValue}
            onClick={() => handleSendClick(inputValue)}
          >
            <img
              src='https://stage.flox.is/images/send.svg'
              alt=''
              className='w-4 h-4'
            />
          </button>
        </div>
      </div>
      <div className='flex flex-wrap gap-2 w-full max-w-[500px]'>
        <PromptButton
          text='Find Warehouse Space'
          icon='https://stage.flox.is/images/find_warehousing_icon.svg'
          handleClick={handleSendClick}
          promptMessage='Please find warehousing services for a Shipper/Vendor. Request a location (city, town, or postcode) to search for available warehouses'
        />
        <PromptButton
          text='Find Logisitics Company by Name'
          icon='https://stage.flox.is/images/find_3PL_icon.svg'
          handleClick={handleSendClick}
          promptMessage='Please find a specific 3PL or a logistics company by name for a Shipper/Vendor. Request the company name to perform a global search'
        />
        <PromptButton
          text='Request Transport or Shipping Quote'
          icon='https://stage.flox.is/images/find_transport_icon.svg'
          handleClick={handleSendClick}
          promptMessage='Please help a Shipper or Vendor request transportation services to move goods between two locations. Ask for collection and delivery postcodes, load details, timing, and vehicle requirements to generate a transport quote.'
        />
        <PromptButton
          text='List Your Services'
          icon='https://stage.flox.is/images/list_logistics_services_icon.svg'
          handleClick={handleSendClick}
          promptMessage='Please guide a Logistics Service Provider on listing their warehouse or logistics services on FLOX'
        />
        <PromptButton
          text='Sector Knowledge'
          icon='https://stage.flox.is/images/sector_knowledge_icon.svg'
          handleClick={handleSendClick}
          promptMessage='Please answer a question about FLOX platform operations, fees, or functionality for a user'
        />
        <PromptButton
          text='How FLOX Works'
          icon='https://stage.flox.is/images/general_enquiries_icon.svg'
          handleClick={handleSendClick}
          promptMessage='Please provide general logistics sector knowledge or handle an unrelated query for a user seeking industry insights'
        />
        <PromptButton
          text='Sign Up with FLOX'
          icon='https://stage.flox.is/images/sign_up_icon_white.svg'
          handleClick={() =>
            window.open(FLOX_REGISTRATION_URL, '_blank')?.focus()
          }
          fullWidth
          primary
        />
      </div>
    </div>
  );
}
